import { useEffect, useState } from 'react';
import { checkPrice, type AcceptanceStatus, type PortalOrderLine, type Urgency, type ValidationStatus } from '@provalia/shared';
import { ApiError } from '../../shared/api';
import { Badge, errorText, useToast, type Tone } from '../../shared/ui';
import { fmtDate, fmtPct } from '../../shared/format';
import { api } from '../api';

/**
 * Estados del portal dichos con palabras. El color solo refuerza: todo
 * distintivo lleva texto, para quien no distingue colores o imprime en B/N.
 */

export const ACCEPTANCE: Record<AcceptanceStatus, { label: string; tone: Tone }> = {
  P: { label: 'Pendiente de aceptar', tone: 'warn' },
  A: { label: 'Aceptado', tone: 'ok' },
  R: { label: 'Rechazado', tone: 'bad' },
};

export const DATE_STATUS: Record<ValidationStatus, { label: string; tone: Tone }> = {
  P: { label: 'Sin proponer', tone: 'neutral' },
  A: { label: 'Confirmada', tone: 'ok' },
  V: { label: 'Pendiente de validar', tone: 'warn' },
  C: { label: 'Aprobada', tone: 'ok' },
  R: { label: 'Rechazada', tone: 'bad' },
};

export const PRICE_STATUS: Record<ValidationStatus, { label: string; tone: Tone }> = {
  P: { label: 'Sin albarán', tone: 'neutral' },
  A: { label: 'Precio conforme', tone: 'ok' },
  V: { label: 'Pendiente de validar', tone: 'warn' },
  C: { label: 'Aprobado por compras', tone: 'ok' },
  R: { label: 'Rechazado por compras', tone: 'bad' },
};

export const URGENCY: Record<Urgency, { label: string; tone: Tone; row: string }> = {
  late: { label: 'Retrasado', tone: 'bad', row: 'urg-late' },
  soon: { label: 'Próximos 15 días', tone: 'warn', row: 'urg-soon' },
  later: { label: 'Resto', tone: 'ok', row: 'urg-later' },
  done: { label: 'Servida', tone: 'neutral', row: 'urg-done' },
};

export function AcceptanceBadge({ status }: { status: AcceptanceStatus }): JSX.Element {
  const s = ACCEPTANCE[status];
  return <Badge tone={s.tone}>{s.label}</Badge>;
}

export function DateStatusBadge({ status }: { status: ValidationStatus }): JSX.Element {
  const s = DATE_STATUS[status];
  return <Badge tone={s.tone}>{s.label}</Badge>;
}

export function PriceStatusBadge({ status }: { status: ValidationStatus }): JSX.Element {
  const s = PRICE_STATUS[status];
  return <Badge tone={s.tone}>{s.label}</Badge>;
}

export function UrgencyBadge({ urgency }: { urgency: Urgency }): JSX.Element {
  const u = URGENCY[urgency];
  return <Badge tone={u.tone}>{u.label}</Badge>;
}

export function ReceiptBadge({ line }: { line: PortalOrderLine }): JSX.Element {
  if (!line.physicalReceipt) return <Badge>Sin recibir</Badge>;
  return <Badge tone="ok">Recibida{line.physicalReceiptAt ? ` ${fmtDate(line.physicalReceiptAt)}` : ''}</Badge>;
}

export function GreenLightBadge({ on }: { on: boolean }): JSX.Element {
  return on ? <Badge tone="ok">Luz verde</Badge> : <Badge>Pendiente</Badge>;
}

/** Leyenda de la cartera: color y texto juntos, nunca solo color. */
export function UrgencyLegend(): JSX.Element {
  return (
    <div className="pt-legend" aria-label="Leyenda de colores">
      <span className="pt-legend-item late">Retrasado</span>
      <span className="pt-legend-item soon">Próximos 15 días</span>
      <span className="pt-legend-item later">Resto</span>
    </div>
  );
}

/**
 * Cotejo del albarán con el pedido (apartado 6.5): pulgar arriba si casa o
 * entra en tolerancia, pulgar abajo si no.
 */
export function PriceThumb({ line, tolerancePct }: { line: PortalOrderLine; tolerancePct: number }): JSX.Element | null {
  if (line.deliveryNotePrice === null) return null;
  const reference = line.originalPrice ?? line.unitPrice;
  const check = checkPrice(reference, line.deliveryNotePrice, tolerancePct);
  const good = line.priceStatus === 'A' || line.priceStatus === 'C';
  const dev = check.exact ? '' : Number.isFinite(check.deviationPct) ? ` ${fmtPct(check.deviationPct)}` : '';
  return (
    <span className={`pt-thumb ${good ? 'ok' : 'bad'}`}>
      <span aria-hidden="true">{good ? '👍' : '👎'}</span> {good ? 'Conforme' : 'Discrepancia'}
      {dev}
    </span>
  );
}

/**
 * Versión del plano y el «ojo» que descarga el ZIP (.pdf, .dwg, .stp) de esa
 * versión (apartado 6.3). El proveedor puede descargarlo antes de aceptar.
 */
export function DrawingCell({ docEntry, lineNum, version }: { docEntry: number; lineNum: number; version: number | null }): JSX.Element {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  if (version === null) return <span className="subtle">—</span>;
  const label = `Descargar plano v${version} (ZIP con PDF, DWG y STEP)`;
  return (
    <span className="row nowrap" style={{ gap: 4, flexWrap: 'nowrap' }}>
      <span className="num">v{version}</span>
      <button
        type="button"
        className="btn ghost sm pt-eye"
        disabled={busy}
        title={label}
        aria-label={label}
        onClick={(e) => {
          e.stopPropagation();
          setBusy(true);
          downloadDrawing(docEntry, lineNum)
            .catch((err: unknown) => toast(errorText(err), 'bad'))
            .finally(() => setBusy(false));
        }}
      >
        {busy ? <span className="spinner pt-spinner-sm" /> : <EyeIcon />}
      </button>
    </span>
  );
}

/**
 * Descarga con la sesión puesta. Se hace el fetch a mano y no con api.blob
 * para poder enseñar el mensaje y la pista que manda la API cuando no hay plano.
 */
async function downloadDrawing(docEntry: number, lineNum: number): Promise<void> {
  const token = api.token();
  let res: Response;
  try {
    res = await fetch(`/api/portal/orders/${docEntry}/lines/${lineNum}/drawing`, {
      headers: token ? { authorization: `Bearer ${token}` } : {},
    });
  } catch {
    throw new ApiError(0, 'No hay conexión con el servidor', 'Comprueba tu conexión a internet y vuelve a intentarlo');
  }
  if (!res.ok) {
    if (res.status === 401) {
      // Que el cliente común cierre la sesión y vuelva a la entrada.
      void api.get('/portal/me').catch(() => undefined);
    }
    let body: { error?: unknown; hint?: unknown } = {};
    try {
      body = (await res.json()) as typeof body;
    } catch {
      /* sin cuerpo JSON */
    }
    throw new ApiError(
      res.status,
      typeof body.error === 'string' ? body.error : `No se pudo descargar el plano (error ${res.status})`,
      typeof body.hint === 'string' ? body.hint : null,
    );
  }
  const blob = await res.blob();
  const match = /filename="?([^";]+)"?/i.exec(res.headers.get('content-disposition') ?? '');
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = match?.[1] ?? `plano_${docEntry}_${lineNum + 1}.zip`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

function EyeIcon(): JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" fill="none" stroke="currentColor" strokeWidth="2" />
      <circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

/**
 * Logo de un proveedor. Va con la sesión puesta (no es público), así que se
 * descarga como Blob y se pinta con una URL local.
 */
export function SupplierLogo({
  cardCode,
  hasLogo,
  name,
  version = 0,
  size,
}: {
  cardCode: string;
  hasLogo: boolean;
  name: string;
  /** Cambiarlo fuerza a volver a descargarlo (tras subir uno nuevo). */
  version?: number;
  size?: number;
}): JSX.Element {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!hasLogo) {
      setUrl(null);
      return;
    }
    let alive = true;
    let objectUrl: string | null = null;
    api
      .blob(`/portal/suppliers/${encodeURIComponent(cardCode)}/logo`)
      .then((b) => {
        if (!alive) return;
        objectUrl = URL.createObjectURL(b);
        setUrl(objectUrl);
      })
      .catch(() => alive && setUrl(null));
    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [cardCode, hasLogo, version]);
  const style = size ? { width: size, height: size } : undefined;
  if (url) return <img className="supplier-logo" src={url} alt={`Logo de ${name}`} style={style} />;
  return (
    <span className="supplier-logo pt-initials" style={style} aria-hidden="true">
      {initials(name)}
    </span>
  );
}

function initials(name: string): string {
  const words = name.replace(/[^\p{L}\p{N} ]/gu, ' ').split(/\s+/).filter(Boolean);
  return ((words[0]?.[0] ?? '?') + (words[1]?.[0] ?? '')).toUpperCase();
}

/** «Línea 1»: el número que dice la API en sus mensajes (lineNum + 1). */
export const lineLabel = (lineNum: number): string => `Línea ${lineNum + 1}`;

/** Fecha que se toma como «requerida» para comparar, igual que hace la API. */
export const requiredOf = (l: PortalOrderLine): string | null => l.requiredDate ?? l.deliveryDate;
